
import { useTranslations } from "use-intl";
import {
  Clock,
  Loader2,
  CheckCircle2,
  XCircle,
  Ban,
  PauseCircle,
} from "lucide-react";
import { StatusChip } from "@/components/ui/status-chip/status-chip";
import type { StatusChipTone } from "@/components/ui/status-chip/status-chip";
import type { RunStatus } from "@/types/workflow.types";

// Agentic step statuses that surface in run badges.
// thinking / tool_running → processing (spinning)
// waiting / waiting_checkpoint → queued
// paused → warning
// skipped_by_decision → queued

type ExtendedStatus = RunStatus | "thinking" | "tool_running" | "waiting_checkpoint" | "waiting" | "paused" | "skipped_by_decision";

interface StatusDef {
  tone: StatusChipTone;
  icon: React.ElementType;
  /** true = icon spins (for older inline rendering path) */
  spin?: boolean;
}

const STATUS_MAP: Record<ExtendedStatus, StatusDef> = {
  // Core run statuses
  pending:          { tone: "queued",     icon: Clock },
  running:          { tone: "processing", icon: Loader2, spin: true },
  success:          { tone: "done",       icon: CheckCircle2 },
  failed:           { tone: "failed",     icon: XCircle },
  cancelled:        { tone: "warning",    icon: Ban },
  paused_checkpoint:{ tone: "warning",    icon: PauseCircle },
  paused_manual:    { tone: "warning",    icon: PauseCircle },

  // Agentic step statuses surfaced in run-level badge contexts
  thinking:           { tone: "processing", icon: Loader2, spin: true },
  tool_running:       { tone: "processing", icon: Loader2, spin: true },
  waiting_checkpoint: { tone: "queued",     icon: Clock },
  waiting:            { tone: "queued",     icon: Clock },
  paused:             { tone: "warning",    icon: PauseCircle },
  skipped_by_decision:{ tone: "queued",     icon: Ban },
};

interface RunStatusBadgeProps {
  status: ExtendedStatus;
}

export default function RunStatusBadge({ status }: RunStatusBadgeProps) {
  const t = useTranslations("workflows");
  const def = STATUS_MAP[status] ?? STATUS_MAP.pending;

  // i18n key — falls back to raw status string if not translated yet.
  const labelKey = `run_status.${status}` as Parameters<typeof t>[0];
  const label = t(labelKey);

  return (
    <StatusChip tone={def.tone}>
      {label}
    </StatusChip>
  );
}
